import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { routePattern } from "@/app/providers/ActivityLog/lib/routePattern";
import { isImpersonating } from "@/features/admin/impersonation/lib/impersonationSession";
import {
    installActivityBridge,
    installCheckoutReturnWatch,
    noteSignedInUser,
    setAnalyticsUser,
    syncAttributionToServer,
    trackPageView,
} from "@/shared/lib/analytics";
import { useUser } from "@/shared/lib/hooks/useUser/useUser";

// A redirect right after mount (<Navigate replace>: /:lang → main page, the
// login gate) changes the path within a tick or two; waiting this long sends
// one page_view for where the person actually landed.
const PAGE_VIEW_SETTLE_MS = 50;
// Login writes site_session_token before setUser — a second try covers the
// paths that write it later.
const ATTRIBUTION_RETRY_MS = 3_000;

/**
 * GA4 through GTM (shared/lib/analytics): page_view on every path change,
 * the activity-log bridge, signed-in state, and the ad-click attribution
 * sent to site_server after sign-in. Rendered before ActivityLogRoot so the
 * bridge is in place before the log starts.
 */
export const AnalyticsRoot = () => {
    const location = useLocation();
    const { user, isAuthenticated } = useUser();
    const owner = typeof user === "string" ? user : user?.email || "";

    useEffect(() => {
        installActivityBridge();
        // checkout_cancel when a tab comes back from Stripe unpaid.
        installCheckoutReturnWatch();
    }, []);

    useEffect(() => {
        const t = setTimeout(
            () => trackPageView({ route: routePattern(window.location.pathname) }),
            PAGE_VIEW_SETTLE_MS,
        );
        return () => clearTimeout(t);
        // A page view is a path change; a new ?q= alone is not.
    }, [location.pathname]);

    useEffect(() => {
        setAnalyticsUser({ is_logged_in: isAuthenticated });
    }, [isAuthenticated]);

    // After sign-in, and on a new ad click while signed in (App.tsx captures
    // it in its own effect, after this one — hence the timers).
    useEffect(() => {
        if (!isAuthenticated || !owner || isImpersonating()) return;
        void noteSignedInUser(owner);
        const send = () => {
            if (!isImpersonating()) void syncAttributionToServer(owner);
        };
        const t1 = setTimeout(send, 0);
        const t2 = setTimeout(send, ATTRIBUTION_RETRY_MS);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [isAuthenticated, owner, location.search]);

    return null;
};
